// src/pages/HomePage.tsx

import React, { useState, useEffect } from 'react';
import {
    Container,
    TextField,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Box,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Button,
    Tooltip,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import jsPDF from 'jspdf';
import ReactDOMServer from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import html2canvas from 'html2canvas';
import { getRecord, listRecords, RecordListItem } from '../api/records';
import { listUsers, AppUser } from '../api/users';
import { useNavigate, useLocation } from 'react-router-dom';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import RecordMetadataPills from '../components/RecordMetadataPills';
import {
    fetchMetadataBulk,
    RecordMetadata,
    fetchAllTags,
} from '../api/metadata';

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 10;

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // filters & pagination
    const [search, setSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [creators, setCreators] = useState<string[]>([]);
    const [page, setPage] = useState(1);
    const [severityFilter, setSeverityFilter] = useState<string | null>(null);
    const [tagFilter, setTagFilter] = useState<string[]>([]);

    // available users for filtering
    const [users, setUsers] = useState<AppUser[]>([]);

    // data
    const [total, setTotal] = useState(0);
    const [firstDate, setFirstDate] = useState('');
    const [lastDate, setLastDate] = useState('');
    const [items, setItems] = useState<RecordListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [filteredTotal, setFilteredTotal] = useState<number | null>(null);
    const [availableTags, setAvailableTags] = useState<string[]>([]);
    const [metadataMap, setMetadataMap] = useState<
        Record<string, RecordMetadata>
    >({});
    const [allClientFilteredItems, setAllClientFilteredItems] = useState<
        RecordListItem[] | null
    >(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // debounce page reset on filter change
    useEffect(() => {
        const handle = setTimeout(() => setPage(1), DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search, dateFrom, dateTo, creators, severityFilter, tagFilter]);

    // fetch list of users for creator filter
    useEffect(() => {
        (async () => {
            try {
                const u = await listUsers();
                setUsers(u);
            } catch (err) {
                console.error('Failed to load users', err);
            }
        })();
    }, []);

    // fetch all tags
    useEffect(() => {
        (async () => {
            try {
                const tags = await fetchAllTags();
                setAvailableTags(tags.sort((a, b) => a.localeCompare(b)));
            } catch (e) {
                console.error('Failed to load tags', e);
                setAvailableTags([]);
            }
        })();
    }, []);

    // fetch data
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const localDateToUtcIso = (
                    localDate: string,
                    endOfDay = false
                ) => {
                    if (!localDate) return undefined;
                    const date = new Date(
                        localDate + (endOfDay ? 'T23:59:59' : 'T00:00:00')
                    );
                    return date.toISOString();
                };

                const data = await listRecords({
                    page,
                    per_page: PAGE_SIZE,
                    search: search || undefined,
                    date_from: localDateToUtcIso(dateFrom),
                    date_to: localDateToUtcIso(dateTo, true),
                    creators: creators.length ? creators : undefined,
                });
                setTotal(data.total);
                setFirstDate(
                    data.first_date ? data.first_date.split('T')[0] : ''
                );
                setLastDate(data.last_date ? data.last_date.split('T')[0] : '');
                setItems(data.items);

                // Fetch metadata in bulk
                const revisionGroupIds = data.items.map(
                    (item) => item.revision_group
                );
                let metadataList: RecordMetadata[] = [];
                if (revisionGroupIds.length > 0) {
                    metadataList = await fetchMetadataBulk(revisionGroupIds);
                }
                const metadataObj = Object.fromEntries(
                    metadataList.map((m) => [m.revision_group_id, m])
                );
                setMetadataMap(metadataObj);
            } catch (err) {
                console.error('Failed to load records or metadata', err);
                setMetadataMap({});
            } finally {
                setLoading(false);
            }
        })();
    }, [search, dateFrom, dateTo, creators, page]);

    // compute TOTAL across all pages after metadata filters
    useEffect(() => {
        (async () => {
            try {
                // Build same base params you already send to listRecords
                const localDateToUtcIso = (
                    localDate: string,
                    endOfDay = false
                ) => {
                    if (!localDate) return undefined;
                    const d = new Date(
                        localDate + (endOfDay ? 'T23:59:59' : 'T00:00:00')
                    );
                    return d.toISOString();
                };

                const baseParams = {
                    search: search || undefined,
                    date_from: localDateToUtcIso(dateFrom),
                    date_to: localDateToUtcIso(dateTo, true),
                    creators: creators.length ? creators : undefined,
                };

                // If no metadata filters, server total is already correct
                if (!severityFilter && tagFilter.length === 0) {
                    setFilteredTotal(total);
                    setAllClientFilteredItems(null);
                    return;
                }

                // Early exit
                if (!total) {
                    setFilteredTotal(0);
                    setAllClientFilteredItems([]);
                    return;
                }

                // Fetch ALL pages using the normal page size
                const pages = Math.ceil(total / PAGE_SIZE);
                const responses = await Promise.all(
                    Array.from({ length: pages }, (_, i) =>
                        listRecords({
                            ...baseParams,
                            page: i + 1,
                            per_page: PAGE_SIZE,
                        })
                    )
                );
                const allItems = responses.flatMap((r) => r.items);

                // Fetch metadata for all records once
                const allRevIds = Array.from(
                    new Set(allItems.map((i) => i.revision_group))
                );
                const metaList = allRevIds.length
                    ? await fetchMetadataBulk(allRevIds)
                    : [];
                const metaMapAll: Record<string, RecordMetadata> =
                    Object.fromEntries(
                        metaList.map((m) => [m.revision_group_id, m])
                    );

                setMetadataMap((prev) => ({ ...prev, ...metaMapAll }));

                // Apply the SAME client filter logic you use for the page
                const clientFilteredList = allItems.filter((rec) => {
                    const meta = metaMapAll[rec.revision_group];
                    if (!meta) return true; // preserve your current behavior
                    const matchesSeverity =
                        !severityFilter || meta.severity === severityFilter;
                    const matchesTags =
                        tagFilter.length === 0 ||
                        tagFilter.every((t) => meta.tags.includes(t));
                    return matchesSeverity && matchesTags;
                });

                setFilteredTotal(clientFilteredList.length);
                setAllClientFilteredItems(clientFilteredList);
            } catch (e) {
                console.error('Failed to compute filtered total', e);
                setFilteredTotal(null);
                setAllClientFilteredItems(null);
            }
        })();
    }, [search, dateFrom, dateTo, creators, severityFilter, tagFilter, total]);

    const onCardClick = (id: string) => {
        navigate(`/records/${id}`, {
            state: { returnPath: location.pathname + location.search },
        });
    };

    const handleExportPdf = async () => {
        // Helper: same date handling you already use elsewhere for consistency
        const toMillis = (isoLike: string) => {
            const s = isoLike.endsWith('Z') ? isoLike : isoLike + 'Z';
            return new Date(s).getTime();
        };

        // 1) Build the list of items to export (ALL matches)
        let exportItems: RecordListItem[] = [];

        if (severityFilter || tagFilter.length > 0) {
            // When metadata filters are active, reuse the already-fetched full filtered list
            exportItems = allClientFilteredItems ?? [];
        } else {
            // No metadata filters: fetch ALL pages using current server-side filters
            const localDateToUtcIso = (localDate: string, endOfDay = false) => {
                if (!localDate) return undefined;
                const d = new Date(
                    localDate + (endOfDay ? 'T23:59:59' : 'T00:00:00')
                );
                return d.toISOString();
            };

            const baseParams = {
                search: search || undefined,
                date_from: localDateToUtcIso(dateFrom),
                date_to: localDateToUtcIso(dateTo, true),
                creators: creators.length ? creators : undefined,
            };

            const pages = Math.ceil(total / PAGE_SIZE) || 0;
            if (pages > 0) {
                const responses = await Promise.all(
                    Array.from({ length: pages }, (_, i) =>
                        listRecords({
                            ...baseParams,
                            page: i + 1,
                            per_page: PAGE_SIZE,
                        })
                    )
                );
                exportItems = responses.flatMap((r) => r.items);
            }
        }

        // 2) Sort oldest -> newest by record_time, tie-break by created_at
        exportItems.sort((a, b) => {
            const rt = toMillis(a.record_time) - toMillis(b.record_time);
            if (rt !== 0) return rt;
            return toMillis(a.created_at) - toMillis(b.created_at);
        });

        // 3) Fetch full records in small batches (8 at a time) with a single retry on failure
        const sleep = (ms: number) =>
            new Promise<void>((r) => setTimeout(r, ms));

        const fetchOneWithRetry = async (
            triplet_id: string
        ): Promise<Awaited<ReturnType<typeof getRecord>>> => {
            try {
                return await getRecord(triplet_id);
            } catch {
                // one quick retry for transient 500/504
                await sleep(500);
                return await getRecord(triplet_id);
            }
        };

        const fetchInBatches = async (
            items: RecordListItem[],
            batchSize = 8
        ): Promise<Awaited<ReturnType<typeof getRecord>>[]> => {
            const results: Awaited<ReturnType<typeof getRecord>>[] = [];
            for (let i = 0; i < items.length; i += batchSize) {
                const slice = items.slice(i, i + batchSize);
                const batch = await Promise.all(
                    slice.map((it) => fetchOneWithRetry(it.triplet_id))
                );
                results.push(...batch);
            }
            return results;
        };

        let fullRecs: Awaited<ReturnType<typeof getRecord>>[];
        try {
            fullRecs = await fetchInBatches(exportItems, 8);
        } catch {
            alert('Export failed while loading records. Please try again.');
            return;
        }

        // 4) Render PDF with page numbers, separators, and no record-splitting
        const pdf = new jsPDF('p', 'pt', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        // Layout constants
        const margin = 36; // page side/top/bottom margin
        const headerY = 22; // page number Y
        const contentTop = margin; // top content start
        const contentBottom = pdfHeight - margin;
        const contentWidth = pdfWidth - margin * 2;

        // Separator styling
        const spacerBeforeRule = 28; // ≈ 2 lines
        const spacerAfterRule = 14; // ≈ 1 line
        const ruleThickness = 1;

        // Page state
        let pageNo = 1;
        let cursorY = contentTop;

        // Draw page header (page number)
        const drawHeader = () => {
            const prevSize = pdf.getFontSize();
            pdf.setFontSize(10);
            pdf.text(`Page ${pageNo}`, pdfWidth - margin, headerY, {
                align: 'right' as const,
            });
            pdf.setFontSize(prevSize);
        };

        // Add new page and reset cursor
        const addNewPage = () => {
            pdf.addPage();
            pageNo += 1;
            drawHeader();
            cursorY = contentTop;
        };

        // Start first page header
        drawHeader();

        for (let i = 0; i < fullRecs.length; i++) {
            const rec = fullRecs[i];

            // Create hidden container
            const container = document.createElement('div');
            container.style.padding = '40px';
            container.style.fontFamily = 'sans-serif';
            container.style.width = '800px';
            container.style.color = '#000';
            container.style.lineHeight = '1.5';
            container.style.backgroundColor = '#fff';
            container.style.boxSizing = 'border-box';

            const section = document.createElement('div');

            // Title
            const title = document.createElement('h2');
            title.textContent = rec.title || '(no title)';
            title.style.fontSize = '24pt';
            title.style.fontWeight = 'bold';
            title.style.margin = '0 0 4px 0';
            section.appendChild(title);

            // Metadata
            const meta = document.createElement('p');
            meta.style.fontSize = '12px';
            meta.style.color = '#555';
            const recordTime = new Date(
                rec.record_time.endsWith('Z')
                    ? rec.record_time
                    : rec.record_time + 'Z'
            ).toLocaleString();
            const loggedTime = new Date(
                rec.created_at.endsWith('Z')
                    ? rec.created_at
                    : rec.created_at + 'Z'
            ).toLocaleString();
            meta.textContent = `Record Time: ${recordTime} | Logged Time: ${loggedTime} | By: ${rec.user_email}`;
            section.appendChild(meta);

            // Markdown content
            const markdownHtml = ReactDOMServer.renderToStaticMarkup(
                <ReactMarkdown>{rec.content || '(no content)'}</ReactMarkdown>
            );
            const contentDiv = document.createElement('div');
            contentDiv.innerHTML = markdownHtml;
            section.appendChild(contentDiv);

            if (rec.attachments?.length > 0) {
                const filenames = rec.attachments.map(
                    (att: { path: string; id: string }) =>
                        att.path.split('/').pop() || att.id
                );
                const attachmentLine = document.createElement('p');
                attachmentLine.style.marginTop = '20px';
                attachmentLine.style.fontWeight = 'bold';

                const label = document.createElement('span');
                label.textContent = 'Attachments: ';
                attachmentLine.appendChild(label);

                const names = document.createElement('span');
                names.style.fontWeight = 'normal';
                names.textContent = filenames.join(', ');
                attachmentLine.appendChild(names);

                section.appendChild(attachmentLine);
            }

            // Add to container and DOM
            container.appendChild(section);
            document.body.appendChild(container);
            container.style.position = 'absolute';
            container.style.left = '-9999px';

            const canvas = await html2canvas(container, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#fff',
            });

            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            const imgProps = pdf.getImageProperties(imgData);

            // Compute scaled size: fit within contentWidth AND available height
            const naturalW = imgProps.width;
            const naturalH = imgProps.height;

            // Width-fit scale
            const scaleW = contentWidth / naturalW;
            let scaledW = contentWidth;
            let scaledH = naturalH * scaleW;

            // If it doesn't fit on a fresh page, scale further to fit height too
            const maxHeightFreshPage = contentBottom - contentTop;
            if (scaledH > maxHeightFreshPage) {
                const scaleH = maxHeightFreshPage / naturalH;
                const scale = Math.min(scaleW, scaleH);
                scaledW = naturalW * scale;
                scaledH = naturalH * scale;
            }

            // If it doesn't fit on the current page, go to next page (no splitting)
            const requiredHeightOnThisPage = scaledH;
            const hasRoom = cursorY + requiredHeightOnThisPage <= contentBottom;
            if (!hasRoom) {
                addNewPage();
            }

            // Draw the record image
            pdf.addImage(imgData, 'JPEG', margin, cursorY, scaledW, scaledH);
            cursorY += scaledH;

            // Separator between records (except after last)
            if (i < fullRecs.length - 1) {
                // If separator won't fit, move to a new page
                const sepNeeded =
                    spacerBeforeRule + ruleThickness + spacerAfterRule;
                if (cursorY + sepNeeded > contentBottom) {
                    addNewPage();
                }

                // Spacer before rule
                cursorY += spacerBeforeRule;

                // Horizontal rule
                pdf.setDrawColor(0);
                pdf.setLineWidth(ruleThickness);
                pdf.line(margin, cursorY, pdfWidth - margin, cursorY);

                // Spacer after rule
                cursorY += spacerAfterRule;
            }

            document.body.removeChild(container);
        }

        const today = new Date().toISOString().slice(0, 10);
        pdf.save(`records_${today}.pdf`);
    };

    // Apply client-side metadata filtering
    const filteredItems = items.filter((rec) => {
        const meta = metadataMap[rec.revision_group];
        if (!meta) return true;

        const matchesSeverity =
            !severityFilter || meta.severity === severityFilter;
        const matchesTags =
            tagFilter.length === 0 ||
            tagFilter.every((t) => meta.tags.includes(t));

        return matchesSeverity && matchesTags;
    });

    // When metadata filters are active, we page over the full filtered set you already computed.
    const isMetaFiltering = !!severityFilter || tagFilter.length > 0;

    // Items to render on the current page:
    // - If meta filters are ON, slice from allClientFilteredItems (full filtered array).
    // - If meta filters are OFF, keep current behavior (server page + per-page client filter).
    const displayedItems = isMetaFiltering
        ? (allClientFilteredItems ?? []).slice(
              (page - 1) * PAGE_SIZE,
              page * PAGE_SIZE
          )
        : filteredItems;

    // Total pages to show in the paginator:
    const paginatorCount = Math.ceil(
        (isMetaFiltering ? (filteredTotal ?? 0) : total) / PAGE_SIZE
    );

    // During the background compute of allClientFilteredItems/filteredTotal, disable pagination.
    const isMetaLoading = isMetaFiltering && filteredTotal === null;

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Filters + Total */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Typography variant="h6">
                    Total&nbsp;:&nbsp;
                    {filteredTotal === null ? '…' : filteredTotal}
                </Typography>

                <TextField
                    label="Search"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                <TextField
                    label="From"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={dateFrom}
                    inputProps={{ max: lastDate || undefined }}
                    onChange={(e) => setDateFrom(e.target.value)}
                />

                <TextField
                    label="To"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={dateTo}
                    inputProps={{ min: firstDate || undefined }}
                    onChange={(e) => setDateTo(e.target.value)}
                />

                {/* Creator multi-select */}
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="creator-label">Creator</InputLabel>
                    <Select
                        labelId="creator-label"
                        multiple
                        value={creators}
                        label="Creator"
                        onChange={(e) =>
                            setCreators(e.target.value as string[])
                        }
                        renderValue={(selected) =>
                            (selected as string[])
                                .map((email) =>
                                    email.replace(/@valaratomics\.com$/, '')
                                )
                                .join(', ')
                        }
                    >
                        {users.map((u) => {
                            const name = u.email.replace(
                                /@valaratomics\.com$/,
                                ''
                            );
                            return (
                                <MenuItem key={u.id} value={u.email}>
                                    <Checkbox
                                        checked={creators.includes(u.email)}
                                    />
                                    <ListItemText primary={name} />
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>

                {/* Severity dropdown */}
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="severity-label">Severity</InputLabel>
                    <Select
                        labelId="severity-label"
                        value={severityFilter ?? ''}
                        label="Severity"
                        onChange={(e) =>
                            setSeverityFilter(e.target.value || null)
                        }
                    >
                        <MenuItem value="">(Any)</MenuItem>
                        {[
                            'Unimportant',
                            'Low',
                            'Medium',
                            'High',
                            'Critical',
                        ].map((level) => (
                            <MenuItem key={level} value={level}>
                                {level}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Tag multi-select */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="tag-label">Tags</InputLabel>
                    <Select
                        labelId="tag-label"
                        multiple
                        value={tagFilter}
                        label="Tags"
                        onChange={(e) =>
                            setTagFilter(e.target.value as string[])
                        }
                        renderValue={(selected) => selected.join(', ')}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 400,
                                },
                            },
                        }}
                    >
                        {availableTags.map((tag) => (
                            <MenuItem key={tag} value={tag}>
                                <Checkbox checked={tagFilter.includes(tag)} />
                                <ListItemText primary={tag} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Clear filters button */}
                <Button
                    onClick={() => {
                        setSearch('');
                        setDateFrom('');
                        setDateTo('');
                        setCreators([]);
                        setSeverityFilter(null);
                        setTagFilter([]);
                    }}
                >
                    Clear Filters
                </Button>

                {/* Export PDF button */}
                <Button
                    size="small"
                    variant="contained"
                    onClick={handleExportPdf}
                    sx={{ ml: 2, whiteSpace: 'nowrap' }}
                >
                    Export PDF
                </Button>
            </Box>
            {/* Record cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {displayedItems.map((rec) => {
                    const recordTime = new Date(
                        rec.record_time.endsWith('Z')
                            ? rec.record_time
                            : rec.record_time + 'Z'
                    ).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        ...(isMobile ? {} : { second: '2-digit' }),
                        hour12: false,
                    });

                    const loggedTime = new Date(
                        rec.created_at.endsWith('Z')
                            ? rec.created_at
                            : rec.created_at + 'Z'
                    ).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        ...(isMobile ? {} : { second: '2-digit' }),
                        hour12: false,
                    });

                    return (
                        <Card
                            key={rec.id}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                boxShadow: 1,
                                overflow: 'hidden',
                                transition: isMobile
                                    ? undefined
                                    : 'transform 0.2s ease-in-out',
                                '&:hover': isMobile
                                    ? undefined
                                    : {
                                          transform: 'scale(1.01)',
                                      },
                            }}
                        >
                            <CardActionArea
                                onClick={() => onCardClick(rec.triplet_id)}
                            >
                                {/* Gradient title header */}
                                <Box
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        px: 2,
                                        py: 1.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                mr: 2,
                                                flexShrink: 1,
                                            }}
                                        >
                                            {rec.title || '(No Title)'}
                                        </Typography>

                                        {!isMobile && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <RecordMetadataPills
                                                    severity={
                                                        metadataMap[
                                                            rec.revision_group
                                                        ]?.severity ?? null
                                                    }
                                                    tags={
                                                        metadataMap[
                                                            rec.revision_group
                                                        ]?.tags ?? []
                                                    }
                                                    isMobile={false}
                                                />
                                                {rec.attachments_count > 0 && (
                                                    <Tooltip
                                                        title={`Has ${rec.attachments_count} attachment(s)`}
                                                    >
                                                        <AttachFileIcon
                                                            fontSize="small"
                                                            color="primary"
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    {isMobile && (
                                        <RecordMetadataPills
                                            severity={
                                                metadataMap[rec.revision_group]
                                                    ?.severity ?? null
                                            }
                                            tags={
                                                metadataMap[rec.revision_group]
                                                    ?.tags ?? []
                                            }
                                            isMobile={true}
                                        />
                                    )}
                                </Box>

                                {/* Main card content */}
                                <CardContent
                                    sx={{
                                        px: 2,
                                        py: 2,
                                        display: 'flex',
                                        flexDirection: isMobile
                                            ? 'column'
                                            : 'row',
                                        alignItems: isMobile
                                            ? 'flex-start'
                                            : 'center',
                                        justifyContent: isMobile
                                            ? 'flex-start'
                                            : 'space-between',
                                        gap: isMobile ? 0 : 7,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Record Time: {recordTime}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        Logged Time: {loggedTime}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ whiteSpace: 'nowrap' }}
                                    >
                                        By: {rec.user_email}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    );
                })}
            </Box>

            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                    count={paginatorCount || 1}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    disabled={loading || isMetaLoading}
                />
            </Box>
        </Container>
    );
};

export default HomePage;
